/**
 * Scheduled messages (Slack "Schedule message", docs/design/slack-gap.md): a message the author composes now and the
 * server posts at `sendAt`, as the author, even if they are offline. Private to the author until it is posted (then
 * it is an ordinary message: `message.created`, notifications, push). Editable, reschedulable, cancellable, or sent
 * at once until then. If posting fails at the time (left the channel, archived), it stays with `failed` set.
 */
import { z } from "zod";
import { FileRef, Id, Timestamp } from "./entities.ts";

/** Latest a message can be scheduled (Slack: 120 days ahead). */
export const SCHEDULE_MAX_AHEAD_MS = 120 * 24 * 60 * 60 * 1000;

export const ScheduledMessage = z.object({
  id: Id,
  channelId: Id,
  /** A thread reply. */
  threadRootId: Id.optional(),
  alsoSendToChannel: z.boolean().optional(),
  text: z.string().max(40_000),
  /** Files that go out with it (uploaded for this conversation; held until it is posted or cancelled). */
  files: z.array(FileRef).optional(),
  sendAt: Timestamp,
  createdAt: Timestamp,
  updatedAt: Timestamp,
  /** Posting at `sendAt` failed: `not_in_channel`, `archived`, `not_found`… (the author fixes it or cancels). */
  failed: z.string().optional(),
});
export type ScheduledMessage = z.infer<typeof ScheduledMessage>;

/** GET /v1/w/:ws/scheduled?channelId= — my scheduled messages (one conversation, or all), soonest first. */
export const ListScheduledQuery = z.object({ channelId: Id.optional() });
export type ListScheduledQuery = z.infer<typeof ListScheduledQuery>;
export const ListScheduledResponse = z.object({ messages: z.array(ScheduledMessage) });
export type ListScheduledResponse = z.infer<typeof ListScheduledResponse>;

/** POST /v1/w/:ws/scheduled — schedule a message (I must be able to post there; `sendAt` in the future, ≤ 120 days). */
export const CreateScheduledRequest = z.object({
  channelId: Id,
  /** The caption; may be empty when `fileIds` has files. */
  text: z.string().max(40_000),
  /** Finished uploads for this conversation (≤ 10), posted with the message. */
  fileIds: z.array(Id).max(10).optional(),
  sendAt: Timestamp,
  threadRootId: Id.optional(),
  alsoSendToChannel: z.boolean().optional(),
});
export type CreateScheduledRequest = z.infer<typeof CreateScheduledRequest>;

/** PATCH /v1/w/:ws/scheduled/:scheduledId — edit the text and/or reschedule (clears `failed`). */
export const UpdateScheduledRequest = z.object({
  text: z.string().min(1).max(40_000).optional(),
  sendAt: Timestamp.optional(),
});
export type UpdateScheduledRequest = z.infer<typeof UpdateScheduledRequest>;

export const ScheduledResponse = z.object({ scheduled: ScheduledMessage });
export type ScheduledResponse = z.infer<typeof ScheduledResponse>;
